import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { Observable } from "rxjs";
import { DestroyBase } from "../../../../components/common/ng-destroy-base";
import { ValuationEventCurrenciesService } from "../../services/valuation-event-currencies.service";
import { EventBusService } from "../../../../services/event-bus.service";
import { AppStorageService } from "../../../../services/app-storage.service";

/**
 * Handles connecting currencies to valuation event
 */
@Component({
  selector: 'valuation-event-currencies',
  templateUrl: './valuation-event-currencies.component.html',
  styleUrls: ['./valuation-event-currencies.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ValuationEventCurrenciesComponent extends DestroyBase implements OnInit {
  /**
 * Gets the valuation portfolio data
 */
  get data(): any { return this._data; }

  /**
   * Sets the valuation portfolio data
   */
  @Input() set data(value: any) {
    this._data = value;
    if (value) {
      this.valuationPortfolioId = value.Oid;
      if (this.lastLoadedValuationPortfolioId != this.valuationPortfolioId) {
        //portfolio changed
        setTimeout(() => {
          this.loadCurrencyConversions();
        });
      }
    } else {
      this.displaySelected();
    }
  }

  /**
 * Should the pane be closed?
 */
  shouldClose = false;

  /**
 * Can be closed? (as Infobar in table view)
 */
  @Input() canClose: boolean;

  /**
 * Close pane event
 */
  @Output() closePane: EventEmitter<any> = new EventEmitter();

  /**
 * List of currencies
 */
  currencies: any[];

  /**
   * ValuationPortfolio Id
   */
  valuationPortfolioId = 0;

  /**
   * Last loaded ValuationPortfolio Id
   */
  lastLoadedValuationPortfolioId = 0;

  /**
  * Is loading in progress?
  */
  loading = false;

  /**
  * Are currencies loaded?
  */
  currenciesLoaded = false;

  /**
* Currencies ref list
*/
  currenciesReflist: any[];

  /**
 * ValuationPortfolio data
 */
  private _data: number;

  /**
* Is external rate dialog opened?
*/
  externalRatesDialogIsOpened = false;

  /**
* dataItemIndexToBeChanged
*/
  dataItemIndexToBeChanged = null;

  /**
* fromCurrencyISOCode for external rate
*/
  fromCurrencyISOCode = "";

  /**
* toCurrencyISOCode for external rate
*/
  toCurrencyISOCode = "";

  /**
* invalid row
*/
  invalid = false;

  /**
* duplicateCurrenciesPairArray
*/
  duplicateCurrenciesPairArray = [];

  /**
* duplicateCurrenciesOneRowArray
*/
  duplicateCurrenciesOneRowArray = [];

  /**
* duplicateCurrenciesPairArray
*/
  emptyConversionRatesArray = [];

  /**
* noProjectCurrencyArray
*/
  noProjectCurrencyArray = [];

  /**
* error messages for invalid rows
*/
  errorMessages = [];

  /**
* duplicateCurrenciesWithReversedOrderArray
*/
  duplicateCurrenciesWithReversedOrderArray = [];

  /**
   * Constructor
   * @param appStorage
   * @param valuationEventCurrenciesService
   * @param eventBus
   * @param translate
   */
  constructor(private appStorage: AppStorageService, private valuationEventCurrenciesService: ValuationEventCurrenciesService, private eventBus: EventBusService, private translate: TranslateService) {
    super();
  }

  /**
 * Initializes the component
 */
  ngOnInit(): void {
    if (!this.data) {
      this.displaySelected();
    }

    this.loadReflist().then(() => {
      this.loadCurrencyConversions();
    });

    this.subscriptions.add(this.valuationEventCurrenciesService.externalRate.subscribe((result) => {
      if (this.dataItemIndexToBeChanged != null) {
        this.currencies[this.dataItemIndexToBeChanged].conversionRate = result;
        this.closeExternalRateDialogClicked();
        this.onChange();
      }
    }));

    this.onEvents.push(
      //another valuation event is selected in grid
      this.eventBus.subscribeEvent('appStorage:setSelectedPortfolio', (item) => {
        setTimeout(() => {
          this.data = item;
        });
      })
    );
  }

  /**
  * Loads currency conversions if the valuation portfolio ID has changed
  */
  loadCurrencyConversions() {
    if (this.lastLoadedValuationPortfolioId != this.valuationPortfolioId) {
      this.lastLoadedValuationPortfolioId = this.valuationPortfolioId;
      this.loading = true;

      // Load currency conversions using the valuation portfolio ID
      this.valuationEventCurrenciesService.loadCurrencyConversions(this.valuationPortfolioId)
        .subscribe(data => {
          this.loading = false;
          this.currencies = data;
          this.currenciesLoaded = true;
          this.invalid = false;
          this.errorMessages = [];
        });
    }
  }

  /**
   * Displays the selected portfolio
   */
  displaySelected() {
    const item = this.appStorage.getSelectedPortfolio();
    if (item) {
      this.data = item;
    }
  }

  /**
   * Deletes a currency row at the specified rowIndex
   * @param currency The currency object to delete
   * @param rowIndex The index of the row to delete
   */
  deleteCurrencyRow(currency: any, rowIndex: number) {
    this.currencies = this.currencies.filter((currency, index) => index !== rowIndex);
    this.onChange();
  }

  /**
   * Adds a new rate with default values to the currencies array
   */
  addRate() {
    this.invalid = true;
    this.currencies.push({
      valuationEventId: this.valuationPortfolioId,
      fromCurrencyId: 0,
      toCurrencyId: 0,
      conversionRate: null,
      clientConversionRate: null
    });
  }

  /**
   * Adds missing rates to the currencies array based on the loaded required rates
   */
  addMissingRates() {
    this.valuationEventCurrenciesService.loadRequiredRates(this.valuationPortfolioId)
      .subscribe((rates) => {
        let rateAdded = false;
        for (const rate of rates) {
          // Check if the rate already exists in currencies
          const exists = this.currencies.filter(c =>
            (c.fromCurrencyId == rate.fromCurrencyId && c.toCurrencyId == rate.toCurrencyId) ||
            (c.toCurrencyId == rate.fromCurrencyId && c.fromCurrencyId == rate.toCurrencyId)
          ).length > 0;

          if (!exists) {
            rateAdded = true;
            this.currencies.push({
              valuationEventId: rate.valuationPortfolioId,
              fromCurrencyId: rate.fromCurrencyId,
              toCurrencyId: rate.toCurrencyId,
              conversionRate: null,
              clientConversionRate: null
            });
          }
        }

        if (rateAdded) {
          this.invalid = true;
          this.onChange();
        } else {
          // Display a dialog indicating that new conversion rate pairs are not needed
          this.eventBus.broadcastEvent("argusDialog", {
            translationKeyTitle: 'newConversionRatesPairsNotNeeded.title',
            success: () => {
              //Nothing
            },
            cancel: () => {
              //Nothing
            },
            dialogContent: () => { return this.translate.instant('newConversionRatesPairsNotNeeded.message') },
            shouldShowCloseButton: () => {
              return true;
            }
          });
        }
      });
  }

  /**
   * Handles the change event
   */
  onChange() {
    this.invalid = false;
    this.duplicateCurrenciesPairArray = [];
    this.duplicateCurrenciesOneRowArray = [];
    this.emptyConversionRatesArray = [];
    this.noProjectCurrencyArray = [];
    this.duplicateCurrenciesWithReversedOrderArray = [];
    this.errorMessages = [];

    setTimeout(() => {
      // duplicate currencies pair check
      this.duplicateCheckPairs();

      // duplicate where order of currencies is reversed
      this.duplicateCheckPairsReversed();

      //pairs of same currency check
      this.duplicateCheckSameCurrency();

      //check if conversion rates are empty
      let emptyConversionRatesCounter = 0;

      this.currencies.forEach((c, index) => {
        if (c.conversionRate == null) {
          emptyConversionRatesCounter++;
          this.invalid = true;
          this.emptyConversionRatesArray.push(index);
        }
        return c.conversionRate == null;
      });

      if (emptyConversionRatesCounter > 0) {
        this.errorMessages.push("Empty conversion rates are not allowed.");
      }
    }, 200);
  }

  /**
   * Duplicate check: Same currency used twice?
   */
  private duplicateCheckSameCurrency(): void {
    const duplicatedColumnEntries = this.currencies.filter((c, index) => {
      if (c.fromCurrencyId === c.toCurrencyId) {
        this.invalid = true;
        this.duplicateCurrenciesOneRowArray.push(index);
      }
      return c.fromCurrencyId === c.toCurrencyId;
    });
    for (const duplicatedColumnEntry of duplicatedColumnEntries) {
      const doubleCurrenciesEntry = this.currenciesReflist.filter((entry) => {
        return duplicatedColumnEntry.fromCurrencyId === entry.oid;
      });
      if (doubleCurrenciesEntry.length > 0) {
        if (!this.errorMessages.includes("Pairs of same currency " + doubleCurrenciesEntry[0].description.split(" -")[0] + "/" + doubleCurrenciesEntry[0].description.split(" -")[0] + " are not allowed.")) {
          this.errorMessages.push("Pairs of same currency " + doubleCurrenciesEntry[0].description.split(" -")[0] + "/" + doubleCurrenciesEntry[0].description.split(" -")[0] + " are not allowed.");
        }
      }
    }
  }

  /**
   * Duplicate check: Same pairs, but used twice, one being reversed the other
   */
  private duplicateCheckPairsReversed(): void {
    for (let currencyRowIndex = 0; currencyRowIndex < this.currencies.length; ++currencyRowIndex) {
      const duplicatedRows = this.currencies.filter((c) => {
        return this.currencies[currencyRowIndex] !== c && c.toCurrencyId === this.currencies[currencyRowIndex].fromCurrencyId && c.fromCurrencyId === this.currencies[currencyRowIndex].toCurrencyId;
      });
      if (duplicatedRows.length > 0) {
        const duplicateFromCurrencyEntry = this.currenciesReflist.filter((entry) => {
          return this.currencies[currencyRowIndex].fromCurrencyId === entry.oid;
        });
        const duplicateToCurrencyEntry = this.currenciesReflist.filter((entry) => {
          return this.currencies[currencyRowIndex].toCurrencyId === entry.oid;
        });
        this.invalid = true;
        this.duplicateCurrenciesWithReversedOrderArray.push(currencyRowIndex);
        if (duplicateFromCurrencyEntry.length > 0 && duplicateToCurrencyEntry.length > 0) {
          if (!this.errorMessages.includes("Pairs of currencies " + duplicateFromCurrencyEntry[0].description.split(" -")[0] + "/" + duplicateToCurrencyEntry[0].description.split(" -")[0] + " with reversed order are not allowed.")) {
            this.errorMessages.push("Pairs of currencies " + duplicateFromCurrencyEntry[0].description.split(" -")[0] + "/" + duplicateToCurrencyEntry[0].description.split(" -")[0] + " with reversed order are not allowed.");
          }
        }
      }
    }
  }

  /**
   * Duplicate check
   */
  private duplicateCheckPairs(): void {
    for (let currencyRowIndex = 0; currencyRowIndex < this.currencies.length; ++currencyRowIndex) {
      const duplicatedRows = this.currencies.filter((c) => {
        return this.currencies[currencyRowIndex] !== c && c.fromCurrencyId === this.currencies[currencyRowIndex].fromCurrencyId && c.toCurrencyId === this.currencies[currencyRowIndex].toCurrencyId;
      });
      if (duplicatedRows.length > 0) {
        const duplicateFromCurrencyEntry = this.currenciesReflist.filter((entry) => {
          return this.currencies[currencyRowIndex].fromCurrencyId === entry.oid;
        });
        const duplicateToCurrencyEntry = this.currenciesReflist.filter((entry) => {
          return this.currencies[currencyRowIndex].toCurrencyId === entry.oid;
        });
        this.invalid = true;
        this.duplicateCurrenciesPairArray.push(currencyRowIndex);
        if (duplicateFromCurrencyEntry.length > 0 && duplicateToCurrencyEntry.length > 0) {
          if (!this.errorMessages.includes("The pair " + duplicateFromCurrencyEntry[0].description.split(" -")[0] + "/" + duplicateToCurrencyEntry[0].description.split(" -")[0] + " is duplicated, you need to remove one of the duplicated lines.")) {
            this.errorMessages.push("The pair " + duplicateFromCurrencyEntry[0].description.split(" -")[0] + "/" + duplicateToCurrencyEntry[0].description.split(" -")[0] + " is duplicated, you need to remove one of the duplicated lines.");
          }
        }
      }
    }
  }

  /**
   * Saves
   */
  save() {
    this.subscriptions.add(this.valuationEventCurrenciesService.saveCurrencyConversions(this.valuationPortfolioId, this.currencies).subscribe((_result) => {
      //Refresh Report grid
      this.eventBus.broadcastEvent("reportsDataSaved");

      //Nothing
      this.closeClicked();
    }));
  }

  /**
* Closes dialog
*/
  closeClicked() {
    this.shouldClose = true;
    setTimeout(() => {
      this.closePane.emit();
    }, 500);
  }

  /**
 * Load the ref lists
 */
  loadReflist(): Promise<void> {
    const sortByDescriptionFn = (item1, item2) => {
      if (item1.description < item2.description) return -1;
      else if (item1.description > item2.description) return 1;
      else return 0;
    }

    return new Promise((resolve, _reject) => {
      this.subscriptions.add(this.requestRefList().subscribe(response => {
        this.currenciesReflist = response.sort(sortByDescriptionFn);
        resolve();
      }));
    });
  }

  /**
 * Ref lists request
 */
  requestRefList(): Observable<any> {
    const currenciesReflist = this.valuationEventCurrenciesService.loadCurrenciesListWithISOCode('ref_currency_with_code');
    return currenciesReflist;
  }

  /**
   * Open the external rates dialog
   * @param dataItem
   * @param index
   */
  openExternalRateDialog(dataItem, index) {
    this.dataItemIndexToBeChanged = index;
    const doubleFromCurrencyEntry = this.currenciesReflist.filter((entry) => {
      return dataItem.fromCurrencyId === entry.oid;
    });
    const doubleToCurrencyEntry = this.currenciesReflist.filter((entry) => {
      return dataItem.toCurrencyId === entry.oid;
    });
    if (doubleFromCurrencyEntry.length > 0) {
      this.fromCurrencyISOCode = doubleFromCurrencyEntry[0].description.split(" -")[0];
    }
    if (doubleToCurrencyEntry.length > 0) {
      this.toCurrencyISOCode = doubleToCurrencyEntry[0].description.split(" -")[0];
    }
    if (doubleFromCurrencyEntry.length > 0 || doubleToCurrencyEntry.length > 0) {
      this.externalRatesDialogIsOpened = true;
    }
  }

  /**
* Closes external rates dialog
*/
  closeExternalRateDialogClicked() {
    this.externalRatesDialogIsOpened = false;
  }
}
